// src/pages/Plantation/tabs/AppelMatinalTab.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserCheck, Plus, CheckCircle, XCircle, Clock,
  Percent, ToggleLeft, ToggleRight, X,
} from 'lucide-react';
import toast from '../../../utils/toast';
import {
  plantationService,
  PlantationAppelMatinal,
  PlantationCampagne,
  PlantationParcelle,
  StatPresence,
} from '../../../services/plantation.service';

// ── Helpers ───────────────────────────────────────────────────────────────────

const todayISO = () => new Date().toISOString().slice(0, 10);

const TYPE_AGENT_BADGE: Record<PlantationAppelMatinal['typeAgent'], string> = {
  MO:          'bg-blue-100 text-blue-700',
  CEAPI:       'bg-purple-100 text-purple-700',
  chauffeur:   'bg-orange-100 text-orange-700',
  superviseur: 'bg-gray-100 text-gray-700',
  autre:       'bg-slate-100 text-slate-600',
};

const TYPE_AGENT_LABEL: Record<PlantationAppelMatinal['typeAgent'], string> = {
  MO:          'Main-d\'œuvre',
  CEAPI:       'CEAPI',
  chauffeur:   'Chauffeur',
  superviseur: 'Superviseur',
  autre:       'Autre',
};

const ACTIVITE_LABEL: Record<PlantationAppelMatinal['activitePrevue'], string> = {
  entretien_sol: 'Entretien sol',
  elagage:       'Élagage',
  fertilisation: 'Fertilisation',
  recolte:       'Récolte',
  collecte:      'Collecte',
  supervision:   'Supervision',
  transport:     'Transport',
  autre:         'Autre',
};

// ── Types internes ────────────────────────────────────────────────────────────

interface AppelMatinalTabProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  parcelles: PlantationParcelle[];
}

interface FormState {
  matricule: string;
  nomAgent: string;
  typeAgent: PlantationAppelMatinal['typeAgent'];
  activitePrevue: PlantationAppelMatinal['activitePrevue'];
  present: boolean;
  heureArrivee: string;
  motifAbsence: string;
  parcelleId: string;
}

const FORM_DEFAULT: FormState = {
  matricule:      '',
  nomAgent:       '',
  typeAgent:      'MO',
  activitePrevue: 'entretien_sol',
  present:        true,
  heureArrivee:   '',
  motifAbsence:   '',
  parcelleId:     '',
};

// ── Composant ─────────────────────────────────────────────────────────────────

export default function AppelMatinalTab({
  entiteId,
  campagnes,
  parcelles,
}: AppelMatinalTabProps) {
  const [date, setDate]             = useState(todayISO());
  const [filterParcelle, setFilterParcelle] = useState('');
  const [appels, setAppels]         = useState<PlantationAppelMatinal[]>([]);
  const [stats, setStats]           = useState<StatPresence | null>(null);
  const [loading, setLoading]       = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [form, setForm]             = useState<FormState>(FORM_DEFAULT);
  const [saving, setSaving]         = useState(false);

  // ── Chargement ─────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!entiteId) return;
    setLoading(true);
    try {
      const [appelData, statsData] = await Promise.all([
        plantationService.listAppelMatinal(entiteId, {
          date,
          parcelleId: filterParcelle || undefined,
        }),
        plantationService.getStatPresence(entiteId, date),
      ]);
      setAppels(Array.isArray(appelData) ? appelData : []);
      setStats(statsData);
    } catch {
      toast.error('Erreur lors du chargement de l\'appel matinal');
    } finally {
      setLoading(false);
    }
  }, [entiteId, date, filterParcelle]);

  useEffect(() => { load(); }, [load]);

  // ── Modal ──────────────────────────────────────────────────────────────────

  const openModal = () => {
    setForm(FORM_DEFAULT);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.matricule.trim()) {
      toast.error('Le matricule est obligatoire');
      return;
    }
    setSaving(true);
    try {
      await plantationService.createAppelMatinal({
        entiteId,
        date,
        matricule:      form.matricule.trim(),
        nomAgent:       form.nomAgent.trim() || undefined,
        typeAgent:      form.typeAgent,
        activitePrevue: form.activitePrevue,
        present:        form.present,
        heureArrivee:   form.present && form.heureArrivee ? form.heureArrivee : undefined,
        motifAbsence:   !form.present && form.motifAbsence.trim() ? form.motifAbsence.trim() : undefined,
        parcelleId:     form.parcelleId || undefined,
      });
      toast.success('Présence enregistrée');
      closeModal();
      load();
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle présent/absent ──────────────────────────────────────────────────

  const togglePresent = async (appel: PlantationAppelMatinal) => {
    try {
      await plantationService.createAppelMatinal({
        ...appel,
        present: !appel.present,
        heureArrivee: !appel.present ? undefined : appel.heureArrivee,
      });
      toast.success('Statut mis à jour');
      load();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // ── KPI cards ─────────────────────────────────────────────────────────────

  const kpiCards = [
    {
      label: 'Total effectif',
      value: stats?.total ?? 0,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-100',
    },
    {
      label: 'Présents',
      value: stats?.presents ?? 0,
      icon: UserCheck,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
    },
    {
      label: 'Taux de présence',
      value: stats ? `${(stats.tauxPresence ?? 0).toFixed(1)} %` : '— %',
      icon: Percent,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-100',
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
        <select
          value={filterParcelle}
          onChange={e => setFilterParcelle(e.target.value)}
          className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        >
          <option value="">Toutes les parcelles</option>
          {parcelles.map(p => (
            <option key={p.id} value={p.id}>{p.code} — {p.nom}</option>
          ))}
        </select>
        <button
          onClick={openModal}
          className="ml-auto flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 rounded transition-colors"
        >
          <Plus size={13} />
          Saisir présence
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        {kpiCards.map(({ label, value, icon: Icon, color, bg, border }) => (
          <div key={label} className={`rounded-lg border ${border} ${bg} p-3 flex items-center gap-3`}>
            <div className={`p-2 rounded-lg bg-white shadow-sm`}>
              <Icon size={16} className={color} />
            </div>
            <div>
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`text-lg font-bold ${color}`}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-28 text-xs text-gray-400">
            Chargement…
          </div>
        ) : appels.length === 0 ? (
          <div className="flex items-center justify-center h-28 text-xs text-gray-400">
            Aucune entrée pour cette date
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Matricule', 'Nom agent', 'Type', 'Activité prévue', 'Présent', 'Heure', 'Motif absence', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {appels.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 font-mono font-medium text-gray-800">{a.matricule}</td>
                    <td className="px-3 py-2 text-gray-700">{a.nomAgent || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${TYPE_AGENT_BADGE[a.typeAgent]}`}>
                        {TYPE_AGENT_LABEL[a.typeAgent]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{ACTIVITE_LABEL[a.activitePrevue]}</td>
                    <td className="px-3 py-2">
                      {a.present
                        ? <CheckCircle size={14} className="text-emerald-500" />
                        : <XCircle size={14} className="text-red-400" />}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {a.heureArrivee
                        ? <span className="flex items-center gap-1"><Clock size={11} />{a.heureArrivee}</span>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500 max-w-[140px] truncate">{a.motifAbsence || '—'}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => togglePresent(a)}
                        title={a.present ? 'Marquer absent' : 'Marquer présent'}
                        className="text-gray-400 hover:text-emerald-600 transition-colors"
                      >
                        {a.present
                          ? <ToggleRight size={16} className="text-emerald-500" />
                          : <ToggleLeft size={16} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal saisie présence */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
              <h2 className="text-sm font-semibold text-gray-800">Saisir une présence</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <form id="appel-form" onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1 min-h-0 space-y-3">

              {/* Matricule */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Matricule <span className="text-red-500">*</span>
                </label>
                <input
                  name="matricule"
                  value={form.matricule}
                  onChange={handleFormChange}
                  required
                  placeholder="ex: MO-0042"
                  className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>

              {/* Nom agent */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nom agent</label>
                <input
                  name="nomAgent"
                  value={form.nomAgent}
                  onChange={handleFormChange}
                  placeholder="Prénom Nom"
                  className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>

              {/* Type agent + Activité prévue */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type agent</label>
                  <select
                    name="typeAgent"
                    value={form.typeAgent}
                    onChange={handleFormChange}
                    className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  >
                    {(Object.keys(TYPE_AGENT_LABEL) as PlantationAppelMatinal['typeAgent'][]).map(k => (
                      <option key={k} value={k}>{TYPE_AGENT_LABEL[k]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Activité prévue</label>
                  <select
                    name="activitePrevue"
                    value={form.activitePrevue}
                    onChange={handleFormChange}
                    className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  >
                    {(Object.keys(ACTIVITE_LABEL) as PlantationAppelMatinal['activitePrevue'][]).map(k => (
                      <option key={k} value={k}>{ACTIVITE_LABEL[k]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Présent */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="present"
                  name="present"
                  checked={form.present}
                  onChange={handleFormChange}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-400"
                />
                <label htmlFor="present" className="text-xs font-medium text-gray-700">Présent</label>
              </div>

              {/* Heure arrivée (si présent) */}
              {form.present && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Clock size={11} className="inline mr-1" />
                    Heure d'arrivée
                  </label>
                  <input
                    type="time"
                    name="heureArrivee"
                    value={form.heureArrivee}
                    onChange={handleFormChange}
                    className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  />
                </div>
              )}

              {/* Motif absence (si absent) */}
              {!form.present && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Motif d'absence</label>
                  <input
                    name="motifAbsence"
                    value={form.motifAbsence}
                    onChange={handleFormChange}
                    placeholder="ex: Maladie, congé…"
                    className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  />
                </div>
              )}

              {/* Parcelle (optionnel) */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Parcelle (optionnel)</label>
                <select
                  name="parcelleId"
                  value={form.parcelleId}
                  onChange={handleFormChange}
                  className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                >
                  <option value="">— Aucune parcelle —</option>
                  {parcelles.map(p => (
                    <option key={p.id} value={p.id}>{p.code} — {p.nom}</option>
                  ))}
                </select>
              </div>

            </form>

            {/* Footer */}
            <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={closeModal}
                className="text-xs px-4 py-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="appel-form"
                disabled={saving}
                className="text-xs px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded transition-colors"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
